"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { useLocale } from "@/components/LocaleProvider";
import { cn } from "@/lib/utils";

export type SlideImage = {
  src: string;
  alt: string;
  containerClassName?: string;
  imgClassName?: string;
  quality?: number;
  sizes?: string;
  unoptimized?: boolean;
};

export function ImageSlideshow({
  images,
  emptyLabel,
  initialIndex = 0,
  transparentBackground = false,
  whiteBackground = false,
}: {
  images: SlideImage[];
  emptyLabel?: string;
  initialIndex?: number;
  transparentBackground?: boolean;
  whiteBackground?: boolean;
}) {
  const { t } = useLocale();
  const effectiveEmptyLabel = emptyLabel ?? t("modal.addImages");
  const clampIndex = (index: number) => {
    if (images.length === 0) return 0;
    const safe = Math.floor(index);
    return ((safe % images.length) + images.length) % images.length;
  };

  const [currentIndex, setCurrentIndex] = useState(() =>
    clampIndex(initialIndex)
  );

  useEffect(() => {
    setCurrentIndex(clampIndex(initialIndex));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialIndex, images.length]);

  useEffect(() => {
    if (images.length <= 1) return;
    const id = window.setInterval(() => {
      setCurrentIndex((i) => (i + 1) % images.length);
    }, 4500);
    return () => window.clearInterval(id);
  }, [images.length]);

  const goPrev = () =>
    setCurrentIndex((i) => (i - 1 + images.length) % images.length);
  const goNext = () => setCurrentIndex((i) => (i + 1) % images.length);

  const bgClass = transparentBackground
    ? "bg-transparent"
    : whiteBackground
      ? "bg-background rounded"
      : "bg-gray-100 dark:bg-white/5 rounded";

  if (images.length === 0) {
    return (
      <div
        className={`w-full h-full min-h-0 flex items-center justify-center p-4 ${bgClass}`}
      >
        <span className="text-gray-400 dark:text-white/40 text-sm">
          {effectiveEmptyLabel}
        </span>
      </div>
    );
  }

  return (
    <div className={`w-full h-full min-h-[260px] flex flex-col ${bgClass}`}>
      <div className="flex-1 min-h-[200px] relative overflow-hidden">
        {images[currentIndex] ? (
          <div
            className={`absolute inset-0 flex items-center justify-center ${
              images[currentIndex].containerClassName ?? "p-4"
            }`}
          >
            <div className="relative w-full h-full">
              <Image
                src={images[currentIndex].src}
                alt={images[currentIndex].alt}
                fill
                sizes={
                  images[currentIndex].sizes ??
                  "(max-width: 1024px) 95vw, 600px"
                }
                quality={images[currentIndex].quality ?? 90}
                unoptimized={images[currentIndex].unoptimized}
                className={cn(
                  "object-contain [image-rendering:-webkit-optimize-contrast]",
                  images[currentIndex].imgClassName
                )}
              />
            </div>
          </div>
        ) : null}

        {images.length > 1 && (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goPrev();
              }}
              className="absolute left-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-background/80 dark:bg-black/40 text-foreground backdrop-blur border border-black/10 dark:border-white/10 grid place-items-center hover:bg-background focus-visible:outline-2 focus-visible:outline-black dark:focus-visible:outline-white focus-visible:outline-offset-2"
              aria-label="Previous slide"
            >
              <span aria-hidden="true">‹</span>
            </button>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goNext();
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-background/80 dark:bg-black/40 text-foreground backdrop-blur border border-black/10 dark:border-white/10 grid place-items-center hover:bg-background focus-visible:outline-2 focus-visible:outline-black dark:focus-visible:outline-white focus-visible:outline-offset-2"
              aria-label="Next slide"
            >
              <span aria-hidden="true">›</span>
            </button>
          </>
        )}
      </div>

      {images.length > 1 && (
        <div className="flex justify-center gap-1 pb-4">
          {images.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex(index);
              }}
              className="h-8 w-4 grid place-items-center"
              aria-label={`${t("sections.goToSlide")} ${index + 1}`}
              aria-current={index === currentIndex ? "true" : undefined}
            >
              <span
                className={`block h-2.5 w-2.5 rounded-full transition-colors ${
                  index === currentIndex
                    ? "bg-black dark:bg-white"
                    : "bg-gray-400 dark:bg-white/40 hover:bg-gray-600 dark:hover:bg-white/60"
                }`}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
